import { Button } from '@sureco/design-system'
import { Modal } from '@surecompanies/core_components'
import { message } from 'antd'
import {
  ActivityLogAction,
  postActivityLog,
} from 'modules/activity-log-service'
import React, { useRef, useState } from 'react'
import { approveCensusFileRowsUpdates } from '../../api/census-enrollment-platform-api-client'
import { CensusFileRowDto } from '../../api/dto/census-file-row.dto'
import { CreateCensusFieldIgnoreRuleRequestDto } from '../../api/dto/census-field-ignore-rule.dto'
import { CensusFileRowStatusEnum } from '../../api/enums/census-file-row-status.enum'
import { CensusFileRowGridData } from '../../types/census-file-row-grid-data'
import { mapGridDataToCensusFileRowDto } from '../../utils/map-grid-data-to-census-file-row-dto'
import { SAVE_CHANGES_BEFORE_APPROVAL_MESSAGE } from '../constants/census-review.constant'
import { isNil } from 'lodash'

interface ApproveSelectedUpdatesButtonProps {
  publicKey: string
  selectedRows: CensusFileRowGridData[]
  handleSubmit: (rows: CensusFileRowGridData[]) => void
  setLoading: (loading: boolean) => void // Prop to update loading state in parent
  hasPendingChanges: boolean
  pendingIgnoreCreates: CreateCensusFieldIgnoreRuleRequestDto[]
  pendingUnignoreRuleIds: number[]
  onApproveSuccess?: () => void
}

const sendForApproval = async (
  rowsToSubmit: CensusFileRowGridData[],
  publicKey: string,
  pendingIgnoreCreates: CreateCensusFieldIgnoreRuleRequestDto[],
  pendingUnignoreRuleIds: number[]
) => {
  const acceptedRows: CensusFileRowDto[] = rowsToSubmit.map((gridRow) => ({
    ...mapGridDataToCensusFileRowDto(gridRow, true),
    status: CensusFileRowStatusEnum.IN_REVIEW,
  }))
  const clientName =
    rowsToSubmit.length > 0 ? rowsToSubmit[0].rowData.ClientName : ''
  const finalRows = acceptedRows.map((row) => ({
    ...row,
    rowData: {
      ...row.rowData,
      ClientName: clientName,
    },
  }))
  const approve = await approveCensusFileRowsUpdates(finalRows, publicKey, {
    ignoreRules:
      pendingIgnoreCreates.length > 0 ? pendingIgnoreCreates : undefined,
    unignoreRuleIds:
      pendingUnignoreRuleIds.length > 0 ? pendingUnignoreRuleIds : undefined,
  })
  return approve
}

const convertSelectedRowsToRowsToApprove = (
  selectedRows: CensusFileRowGridData[]
): CensusFileRowGridData[] => {
  return selectedRows.map((row) => {
    const newRow = {
      ...row,
      memberUpdates: {
        ...row.memberUpdates,
      },
    }

    // When the reviewer opts out of creating a new enrollment window, the
    // proposed BED / OEP Start / OEP End must NOT be backfilled from the
    // computed updates — leaving them undefined keeps the member's existing
    // enrollment dates so the sync trigger reuses the existing enrollment.
    const doNotCreateEnrollment = Boolean(
      row.memberUpdates?.doNotCreateEnrollment
    )

    if (
      !doNotCreateEnrollment &&
      !isNil(row.updates?.enrollmentStartDate) &&
      isNil(row.memberUpdates?.enrollmentStartDate)
    ) {
      newRow.memberUpdates.enrollmentStartDate =
        row.updates?.enrollmentStartDate
    }
    if (
      !doNotCreateEnrollment &&
      !isNil(row.updates?.enrollmentEndDate) &&
      isNil(row.memberUpdates?.enrollmentEndDate)
    ) {
      newRow.memberUpdates.enrollmentEndDate = row.updates?.enrollmentEndDate
    }

    if (
      !isNil(row.updates?.benefitsEligible) &&
      isNil(row.memberUpdates?.benefitsEligible)
    ) {
      newRow.memberUpdates.benefitsEligible = row.updates?.benefitsEligible
    }
    if (
      !doNotCreateEnrollment &&
      !isNil(row.updates?.benefitsEffectiveDate) &&
      isNil(row.memberUpdates?.benefitsEffectiveDate)
    ) {
      newRow.memberUpdates.benefitsEffectiveDate =
        row.updates?.benefitsEffectiveDate
    }

    return newRow
  })
}

const ApproveSelectedUpdatesButton = ({
  selectedRows,
  publicKey,
  handleSubmit,
  setLoading,
  hasPendingChanges,
  pendingIgnoreCreates,
  pendingUnignoreRuleIds,
  onApproveSuccess,
}: ApproveSelectedUpdatesButtonProps) => {
  const [loading, setButtonLoading] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  // Synchronous guard against duplicate submissions during the 800ms window
  // before `buttonLoading` is set. State updates can't be used here because
  // they're batched and the setTimeout delays the visible loading state.
  const isSubmittingRef = useRef(false)

  const handleApproveClick = () => {
    if (hasPendingChanges) {
      alert(SAVE_CHANGES_BEFORE_APPROVAL_MESSAGE)
      return
    }
    setShowConfirmModal(true)
  }

  const confirmApprove = async () => {
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true

    let timer: NodeJS.Timeout | null = null
    let shouldShowLoading = false

    // Show delayed loading state
    timer = setTimeout(() => {
      setButtonLoading(true)
      setLoading(true)
      shouldShowLoading = true
    }, 800)

    try {
      const rowsToApprove = convertSelectedRowsToRowsToApprove(selectedRows)
      const approve = await sendForApproval(
        rowsToApprove,
        publicKey,
        pendingIgnoreCreates,
        pendingUnignoreRuleIds
      )
      if (approve.errors.length > 0 && approve.failedRows > 0) {
        message.error(`Error updating ${approve.failedRows} rows`)
      } else {
        message.success(`${selectedRows.length} records approved successfully`)
        onApproveSuccess?.()
        postActivityLog({
          action: ActivityLogAction.CENSUS_REVIEW_APPROVE,
          description: `approved ${selectedRows.length} census review records`,
          additionalData: {
            publicKey,
            recordCount: selectedRows.length,
            rows: selectedRows,
          },
        })
      }
    } catch (error) {
      message.error(`Failed to submit records: '${(error as Error).message}'`)
    } finally {
      if (shouldShowLoading) {
        setButtonLoading(false)
        setLoading(false)
      }
      if (timer) clearTimeout(timer)

      setShowConfirmModal(false)
      handleSubmit(selectedRows)
      isSubmittingRef.current = false
    }
  }
  if (selectedRows.length === 0) return null

  return (
    <>
      <Button onClick={handleApproveClick} size="large">
        {loading ? 'Submitting...' : `Approve (${selectedRows.length})`}
      </Button>

      <Modal
        open={showConfirmModal}
        title="Confirm Approval"
        onCancel={() => setShowConfirmModal(false)}
        onOk={confirmApprove}
        okText="Yes, Continue"
        cancelText="No, Cancel"
      >
        <p style={{ textAlign: 'center' }}>Are you sure?</p>
        <p style={{ textAlign: 'center' }}>
          Approving these records will immediately update the record in our
          system and send a closed Zendesk ticket to the team to track these
          updates.
        </p>
      </Modal>
    </>
  )
}

export default ApproveSelectedUpdatesButton
